import React, {useMemo} from 'react';

import {DataFrame, DataFrameRow} from 'data-frames/DataFrameTypes';
import addColumn from 'data-frames/util/addColumn';
import {DataFrameValueAccessor} from 'data-frames/util/createAccessors';
import filterColumns from 'data-frames/util/filterColumns';
import {sortDataFrameByAccessors} from 'data-frames/util/sortDataFrame';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {VisualizationType} from 'insights/analytics-visualizations/AnalyticsVisualizationTypes';
import makeVisualizationConfig from 'insights/universal-blocks/makeVisualizationConfig';
import {AllDataColumnsMapping} from 'machine-learning/parameter-optimization/legacy-components/ParameterOptimizationTypes';

import ModelingDashboardBlock, {SharedModelingBlockProps} from './ModelingDashboardBlock';

export const PREDICTIONS_AND_LABELS_BLOCK_TITLE = 'Predicted and actual outputs by data point';
// TODO(BNCH-47779): Add 'Click on a row to drill down in "Local explanations"' to the description once we support
// click drill downs.
const PREDICTIONS_AND_LABELS_BLOCK_DESCRIPTION = '';

/** @package */
export const VISUALIZATION_TYPE = VisualizationType.TABLE;
/** @package */
export const BASE_VISUALIZATION_PARAMETERS = {
  displayColumnsJson: null,
};
/** @package */
export const LOCAL_EXPLANATIONS_AVAILABLE_SYSTEM_NAME = 'local_explanations_available';
/** @package */
export const LOCAL_EXPLANATIONS_AVAILABLE_COLUMN_DEF = {
  displayName: 'Local Explanations Available',
  systemName: LOCAL_EXPLANATIONS_AVAILABLE_SYSTEM_NAME,
  type: {leafType: LeafType.BOOLEAN},
};

/**
 * Given the all data and shap values data frames, return the data frame to render the predictions and
 * features table.
 *
 * To generate the visualization data frame, perform the following transformations to the all data data frame:
 * 1. Add a `Local Explanations Available` boolean column that denotes if the row has any SHAP values
 * 2. Remove the `features` column.
 * 3. Sort so that rows with true values in `Local Explanations Available` are at the front
 *
 * @package
 */
export function makeVisualizationData(
  allDataDataFrame: DataFrame,
  shapValuesDataFrame: DataFrame,
  dataPointIdAccessor: DataFrameValueAccessor<string>,
  columnsMapping: AllDataColumnsMapping
): DataFrame {
  const dataPointIdsWithShapValues = new Set(shapValuesDataFrame.rows.map((row) => dataPointIdAccessor(row)));
  const allDataWithLocalExplanationsFlag = addColumn(
    allDataDataFrame,
    LOCAL_EXPLANATIONS_AVAILABLE_COLUMN_DEF,
    (row: DataFrameRow) => dataPointIdsWithShapValues.has(dataPointIdAccessor(row))
  );
  const allDataWithFeaturesRemoved = filterColumns(
    allDataWithLocalExplanationsFlag,
    ({systemName}) => systemName !== columnsMapping.features
  );
  return sortDataFrameByAccessors(allDataWithFeaturesRemoved, [
    (row) => (row[LOCAL_EXPLANATIONS_AVAILABLE_SYSTEM_NAME] ? 0 : 1),
  ]);
}

/** @package */
export interface PredictionsAndLabelsBlockProps extends SharedModelingBlockProps {
  allDataDataFrame: DataFrame;
  columnsMapping: AllDataColumnsMapping;
  dataPointIdAccessor: DataFrameValueAccessor<string>;
  shapValuesDataFrame: DataFrame;
}

export default function PredictionsAndLabelsBlock({
  allDataDataFrame,
  columnsMapping,
  dataPointIdAccessor,
  shapValuesDataFrame,
  ...sharedModelingBlockProps
}: PredictionsAndLabelsBlockProps): JSX.Element {
  const visualizationConfig = makeVisualizationConfig(VISUALIZATION_TYPE, BASE_VISUALIZATION_PARAMETERS);

  const dataFrameWithoutFeatures = useMemo(
    () => makeVisualizationData(allDataDataFrame, shapValuesDataFrame, dataPointIdAccessor, columnsMapping),
    [allDataDataFrame, shapValuesDataFrame, dataPointIdAccessor, columnsMapping]
  );

  return (
    <ModelingDashboardBlock
      {...sharedModelingBlockProps}
      blockTitle={PREDICTIONS_AND_LABELS_BLOCK_TITLE}
      blockDescription={PREDICTIONS_AND_LABELS_BLOCK_DESCRIPTION}
      inputDataFrame={dataFrameWithoutFeatures}
      visualizationConfig={visualizationConfig}
    />
  );
}
